import { useState } from "react";
import CallRecordings from "./CallRecordings";
import SidecartSection from "./SidecartSection";

const LIGHTBULB =
  "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z";

const PRIORITY = {
  3: { label: "HIGH PRIORITY", className: "bg-red-100 text-red-800 border-red-400" },
  2: { label: "STANDARD", className: "bg-yellow-100 text-yellow-800 border-yellow-400" },
  1: { label: "ROUTINE", className: "bg-blue-100 text-blue-800 border-blue-400" },
};

const formatSentAt = (iso) =>
  new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

// What the lead came in with: the call recording and transcript for a call,
// the latest patient texts for a text, the form message for a form. The
// texts and form message come from the lead detail response.
const sourceContent = (lead) => {
  if (lead.type === "phonecall" && lead.call_recordings?.length > 0) {
    return <CallRecordings lead={lead} />;
  }
  if (lead.type === "sms" && lead.recent_patient_texts?.length > 0) {
    return (
      <ul className="space-y-2">
        {lead.recent_patient_texts.map((text) => (
          <li key={text.id} className="rounded-lg bg-gray-50 px-3 py-2">
            <p className="ph-mask text-sm text-gray-800 whitespace-pre-wrap">{text.content}</p>
            <p className="mt-0.5 text-xs text-gray-500">{formatSentAt(text.sent_at)}</p>
          </li>
        ))}
      </ul>
    );
  }
  if (lead.form_message) {
    return (
      <p className="ph-mask rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words">
        {lead.form_message}
      </p>
    );
  }
  return null;
};

// "About this lead", shared by the side pane and the full lead page: the AI
// summary, then the source content. Open by default, so staff understand the
// lead before they act. Absent when there is nothing to show.
const AboutLead = ({ lead }) => {
  const [expanded, setExpanded] = useState(true);
  const source = sourceContent(lead);
  const priority = PRIORITY[lead.ai_score];
  if (!lead.ai_summary && !source && !priority) return null;

  return (
    <SidecartSection
      id="about"
      title="About this lead"
      icon={LIGHTBULB}
      expanded={expanded}
      onToggle={() => setExpanded(!expanded)}
      action={
        priority && (
          <span
            className={`px-2 py-0.5 inline-flex items-center justify-center text-xs font-bold rounded whitespace-nowrap border ${priority.className}`}
          >
            {lead.ai_score === 3 && lead.ai_is_urgent && "⚠ "}
            {priority.label}
          </span>
        )
      }
    >
      <div className="space-y-3">
        {lead.ai_summary && (
          <p className="ph-mask text-sm text-gray-700 leading-relaxed">{lead.ai_summary}</p>
        )}
        {source}
      </div>
    </SidecartSection>
  );
};

export default AboutLead;
